import classnames from "classnames";
import React from "react";
import CanvasDraw from "react-canvas-draw";
import OnOffDisplay from "./OnOffDisplay";

export interface ICountable {
  active?: boolean;
  count: number;
}

interface IState {
  color: string;
  coords: {
    x: number;
    y: number;
  };
  btnDefaultCount: number;
  btnFixCount: number;
  firstDistanceSinceMoveStart: number;
  highlightChange?: boolean;
  isMoving: ICountable;
  isTouching: ICountable;
  contextOpened: ICountable;
  clicked: ICountable;
  activity: {
    counting: boolean;
    duration: number;
  };
}

const isKerres = window.innerHeight > window.innerWidth;

class App extends React.Component<{}, IState> {
  private canvasRef: any = null;
  private activityTimeout: NodeJS.Timer | undefined;
  private fixedButtonRef: HTMLButtonElement | null = null;

  constructor(state: Object, context: Object) {
    super(state, context);

    this.clearDrawing = this.clearDrawing.bind(this);

    this.state = {
      coords: { x: 0, y: 0 },
      firstDistanceSinceMoveStart: 0,
      activity: {
        counting: false,
        duration: 0,
      },
      color: "#ffc600",
      clicked: { count: 0 },
      contextOpened: { count: 0 },
      isMoving: { count: 0 },
      isTouching: { count: 0 },
      btnFixCount: 0,
      btnDefaultCount: 0,
    };
  }

  componentDidMount() {
    this.fixedButtonRef?.addEventListener("touchmove", this.ignoreEvent, { passive: false });
  }

  componentWillUnmount() {
    this.fixedButtonRef?.removeEventListener("touchmove", this.ignoreEvent);
  }

  private ignoreEvent = (ev: any) => {
    ev.stopPropagation();
    ev.preventDefault();
  };

  private triggerActivityUpdate = () => {
    if (!this.state.activity.counting) {
      return;
    }

    this.setState({
      activity: {
        counting: true,
        duration: this.state.activity.duration + 1,
      },
    });
    setTimeout(this.triggerActivityUpdate, 1000);
  };

  private triggerActivityWatchdog = () => {
    if (!this.state.activity.counting) {
      this.setState({
        activity: {
          counting: true,
          duration: 0,
        },
      });

      setTimeout(this.triggerActivityUpdate, 1000);
    }

    this.activityTimeout && clearInterval(this.activityTimeout);
    this.activityTimeout = setTimeout(() => {
      this.setState({
        activity: {
          ...this.state.activity,
          counting: false,
        },
      });
    }, 1000);
  };

  private clearDrawing() {
    this.canvasRef.clear();
  }

  private changeColor = () =>
    this.setState({
      color: "#" + Math.floor(Math.random() * 16777215).toString(16),
    });

  // private logEvent: React.MouseEventHandler = (ev: any) => console.log(ev.type);

  private startMove: React.TouchEventHandler = (ev) => {
    this.triggerActivityWatchdog();
    let count = this.state.isMoving.count;

    const xyChange = this.buildXyChangeContent(ev);

    let distance: number;
    if (!this.state.isMoving.active) {
      count++;
      const distanceX = Math.abs(this.state.coords.x - xyChange.coords.x);
      const distanceY = Math.abs(this.state.coords.y - xyChange.coords.y);
      distance = Math.sqrt(Math.pow(distanceX, 2) + Math.pow(distanceY, 2));
    } else {
      distance = this.state.firstDistanceSinceMoveStart ?? 0;
    }

    this.setState({ isMoving: { active: true, count }, firstDistanceSinceMoveStart: distance, ...xyChange });
  };

  private startTouch: React.TouchEventHandler = (ev) => {
    this.triggerActivityWatchdog();
    const isTouching = {
      active: true,
      count: this.state.isTouching.count + 1,
    };

    this.setState({ isTouching, ...this.buildXyChangeContent(ev) });
  };

  private stopTouch: React.TouchEventHandler = (ev) => {
    this.triggerActivityWatchdog();
    const isTouching = {
      ...this.state.isTouching,
      active: false,
    };

    const isMoving = {
      ...this.state.isMoving,
      active: false,
    };

    this.setState({ isTouching, isMoving, highlightChange: false });
  };

  private buildXyChangeContent(ev: React.TouchEvent): Pick<IState, "highlightChange" | "coords">;
  private buildXyChangeContent(ev: React.MouseEvent): Pick<IState, "highlightChange" | "coords">;
  private buildXyChangeContent(ev: React.MouseEvent | React.TouchEvent): Pick<IState, "highlightChange" | "coords"> {
    if (ev.type.startsWith("touch")) {
      return {
        coords: {
          x: (ev as any).targetTouches[0].clientX,
          y: (ev as any).targetTouches[0].clientY,
        },
        highlightChange: true,
      };
    }

    return {
      coords: {
        x: (ev as any).clientX,
        y: (ev as any).clientY,
      },
      highlightChange: true,
    };
  }

  private indicateContext: React.MouseEventHandler = (ev) => {
    this.triggerActivityWatchdog();
    const count = this.state.contextOpened.count + 1;

    this.setState({ contextOpened: { active: true, count }, ...this.buildXyChangeContent(ev) });
    setTimeout(() => {
      const contextOpened = {
        ...this.state.contextOpened,
        active: false,
      };
      this.setState({ contextOpened, highlightChange: false });
    }, 500);

    ev.preventDefault();
    return false;
  };

  private indicateClick: React.MouseEventHandler = (ev) => {
    console.log("click");
    this.triggerActivityWatchdog();
    const count = this.state.clicked.count + 1;

    console.log(this.state.clicked.count, "y");
    this.setState({ clicked: { active: true, count }, ...this.buildXyChangeContent(ev) });
    setTimeout(() => {
      const clicked = {
        ...this.state.clicked,
        active: false,
      };
      this.setState({ clicked, highlightChange: false });
    }, 500);
  };

  private reset: React.MouseEventHandler = (ev) => {
    // Ensure that clicks are not counted when reseting
    ev.stopPropagation();

    const resetCount = (countable: ICountable): ICountable => ({
      ...countable,
      count: 0,
    });

    this.setState({
      activity: {
        counting: false,
        duration: 0,
      },
      clicked: resetCount(this.state.clicked),
      contextOpened: resetCount(this.state.contextOpened),
      isMoving: resetCount(this.state.isMoving),
      isTouching: resetCount(this.state.isTouching),
      btnFixCount: 0,
      btnDefaultCount: 0,
    });
  };

  private trunc = (value: number, decimals: number = 2) => {
    const factor = Math.pow(10, decimals);
    return Math.trunc(value * factor) / factor;
  };

  private help = () => {
    alert(
      "- * = not in drawing area\n" +
        "- touching = touch is currently pressed\n" +
        "- moving = drag and drop active\n-x/y = coordinates of last action\n" +
        "- min. move dist. = attempt to calculate the distance of x/y between touchstart and first touchmove event\n" +
        "- context = context menu would have opened and cancelled click (surpressed in demo)\n" +
        "- clicked = real click event detected\n" +
        "- activity time = very rough estimation of seconds passed during touch operations (timeout for registering further events = 1s)\n" +
        "- Test:no fix = normal button behavior for testing purposes\n" +
        "- Test:fix = changed button behavior to also work with context and move event"
    );
  };

  render() {
    const orientation = isKerres ? "portrait" : "landscape";
    return (
      <div
        className={classnames("app", orientation)}
        onTouchStart={this.startTouch}
        onTouchEnd={this.stopTouch}
        onTouchCancel={this.stopTouch}
        onTouchMove={this.startMove}
        onPointerDown={this.changeColor}
        onClick={this.indicateClick}
        onContextMenu={this.indicateContext}>
        <div className="events">
          <OnOffDisplay label="Touching" {...this.state.isTouching} />
          <OnOffDisplay label="Moving" {...this.state.isMoving} />
          <span>
            <b>X/Y:</b>
          </span>
          <span
            className={classnames({
              "text-highlight": this.state.highlightChange,
              "text-muted": !this.state.highlightChange,
            })}>
            {this.trunc(this.state.coords.x ?? 0, 0)} / {this.trunc(this.state.coords.y ?? 0, 0)}
          </span>

          <span>
            <b>Min. move dist.:</b>
          </span>
          <span
            className={classnames({
              "text-highlight": this.state.highlightChange,
              "text-muted": !this.state.highlightChange,
            })}>
            {this.trunc(this.state.firstDistanceSinceMoveStart)} px
          </span>

          <OnOffDisplay label="Context *" {...this.state.contextOpened} />
          <OnOffDisplay label="Clicked *" {...this.state.clicked} />

          <span>
            <b>Activity Time:</b>
          </span>
          <span
            className={classnames({
              "text-highlight": this.state.activity.counting,
              "text-muted": !this.state.activity.counting,
            })}>
            {this.state.activity.duration}s
          </span>
        </div>
        <div className={classnames("buttons1", orientation)}>
          <button onClick={() => this.setState({ btnDefaultCount: this.state.btnDefaultCount + 1 })}>
            Test: default ({this.state.btnDefaultCount})
          </button>
          <button
            ref={(ref) => (this.fixedButtonRef = ref)}
            onPointerUp={() => this.setState({ btnFixCount: this.state.btnFixCount + 1 })}>
            Test: optimized ({this.state.btnFixCount})
          </button>
        </div>
        <div className={classnames("buttons2", orientation)}>
          <button onClick={this.help} disabled>
            Quick Help
          </button>
          <button onClick={this.reset}>Reset Counter</button>
          <button onClick={this.clearDrawing}>Clear Drawing</button>
        </div>
        <CanvasDraw
          className="drawing"
          ref={(canvasDraw: any) => (this.canvasRef = canvasDraw)}
          brushColor={this.state.color}
          brushRadius={5}
          canvasWidth={isKerres ? window.innerWidth : 400}
          canvasHeight={isKerres ? 700 : window.innerHeight}
        />
      </div>
    );
  }
}

export default App;
